import AWS from 'aws-sdk';
import { documentTypes, docIdToFriendlyName } from '@ourbranch/be-constants';
import { getPolicyStatus, PolicyStatus } from '@ourbranch/policy-utils';
import { get } from '@ourbranch/dynamo-helpers';
const lambda = new AWS.Lambda({ apiVersion: '2015-03-31' });
const getEscrowInvoiceData = async (policies, bindDetails, paidToDate) => {
    const escrowPaidPolicy = policies.find((policy) => policy.segments.some((segment) => Array.isArray(segment.home?.mortgageDetails) && segment.home.mortgageDetails.length > 0));
    console.log(`Getting escrow invoice data for: ${escrowPaidPolicy.id}`);
    const policyStatus = getPolicyStatus(escrowPaidPolicy);
    console.log(`Policy status: ${policyStatus}`);
    const invoicePremiumData = {
        premium: 0,
        fees: 0,
        paidToDate: 0,
        surplusContribution: 0
    };
    // if the policy is cancelled or in cancellation we need to get the premium and the fees for the prior version to avoid showing 0
    if (policyStatus === PolicyStatus.Cancelled || policyStatus === PolicyStatus.InCancellation) {
        console.log(`Getting policy audit for: ${JSON.stringify({
            versionId: escrowPaidPolicy.version - 1,
            policyId: escrowPaidPolicy.id
        }, null, 2)}`);
        const policyAudit = await get('policyaudit', {
            versionId: `${escrowPaidPolicy.version - 1}`,
            policyId: escrowPaidPolicy.id
        });
        console.log(`Retrieved policyAudit version: ${policyAudit.versionId}`);
        invoicePremiumData.premium = policyAudit.premium;
        invoicePremiumData.fees = policyAudit.fees;
        invoicePremiumData.paidToDate = paidToDate;
        invoicePremiumData.surplusContribution = policyAudit.surplusContribution;
    }
    else {
        invoicePremiumData.premium = escrowPaidPolicy.premium;
        invoicePremiumData.fees = escrowPaidPolicy.fees;
        invoicePremiumData.paidToDate = paidToDate;
        invoicePremiumData.surplusContribution = escrowPaidPolicy.surplusContribution;
    }
    console.log(`Invoice premium data: ${JSON.stringify(invoicePremiumData)}`);
    return { policies, bindDetails, invoicePremiumData };
};
const getPolicyDocuments = async (policies, bindDetails, paidToDate) => {
    console.log('[datadog] Generation policy documents for policies: ', JSON.stringify(policies));
    // @TODO this will generate the invoice twice in some cases
    const generateInvoice = policies.some((policy) => policy.segments.some((segment) => Array.isArray(segment.home?.mortgageDetails) && segment.home.mortgageDetails.length > 0));
    const docs = [
        { docType: documentTypes.DECLARATION, data: { policies, bindDetails } },
        { docType: documentTypes.ID_CARDS, data: { policies, bindDetails } },
        { docType: documentTypes.POLICY_JACKET, data: { policies, bindDetails } }
    ];
    if (generateInvoice) {
        const invoiceData = await getEscrowInvoiceData(policies, bindDetails, paidToDate);
        docs.push({
            docType: documentTypes.ESCROW_INVOICE,
            data: invoiceData
        });
        docs.push({
            docType: documentTypes.REPLACEMENT_COST_ESTIMATE,
            data: { policies, bindDetails }
        });
    }
    return docs;
};
const _generate = (docs, docsRenderer, overrides = {}) => {
    console.log(`running generate to ${docsRenderer}`);
    console.log('the following overrides will be applied', overrides);
    const documentsPromise = docs.map((documentData) => lambda
        .invoke({
        FunctionName: docsRenderer,
        InvocationType: 'RequestResponse',
        Payload: JSON.stringify({ ...documentData, overrides })
    })
        .promise());
    return Promise.all(documentsPromise);
};
export const generatePolicyDocuments = async (policies, bindDetails, docsRenderer, paidToDate, isAgencyUser) => {
    try {
        console.log('[datadog] Generating policy documents for: ', JSON.stringify(policies));
        const policyDocs = await getPolicyDocuments(policies, bindDetails, paidToDate);
        console.log(`Generating ${policyDocs.map((x) => x.docType).join(', ')} documents`);
        console.log(`Sending bindDetails of ${JSON.stringify(bindDetails, null, 2)}`);
        const overrides = {};
        if (isAgencyUser) {
            overrides.skipEmailMemberDocURLSegmentEvent = true;
        }
        const generatedDocs = await _generate(policyDocs, docsRenderer, overrides);
        const docs = policyDocs.map((doc, i) => {
            if (generatedDocs[i]) {
                doc.paths =
                    typeof generatedDocs[i].Payload === 'string'
                        ? JSON.parse(generatedDocs[i].Payload)
                        : generatedDocs[i].Payload;
            }
            return doc;
        });
        console.log('Policy documents generated', docs);
        return docs;
    }
    catch (er) {
        console.log('Error generating policy documents', er);
        throw er;
    }
};
export const generateCancellationDocuments = async (policies, bindDetails, docsRenderer, paidToDate) => {
    try {
        const cancellationDocs = await getPolicyDocuments(policies, bindDetails, paidToDate);
        cancellationDocs.push({
            docType: documentTypes.CANCELLATION,
            data: { policies, bindDetails }
        });
        console.log(`Generating ${cancellationDocs.length} documents with ${docsRenderer} (${JSON.stringify(docsRenderer, null, 4)})`);
        const generatedDocs = await _generate(cancellationDocs, docsRenderer);
        console.log('Cancellation documents generated', generatedDocs);
    }
    catch (er) {
        console.log('Error generating cancellation documents', er);
        throw er;
    }
};
export const generateFriendlyName = (fileName) => {
    const docIdWithoutExt = fileName // '2019-2-14-19-29-A_IDCD.pdf'
        .split('.'); // [2019-2-14-19-29-A_IDCD, pdf']
    let docId = docIdWithoutExt[0]
        .split('-') // ['2019', '2', '14', '19', '29', 'A_IDCD']
        .slice(-1)[0] // ['A_IDCD']
        .toUpperCase(); // 'A_IDCD'
    // also drop out "BF_" if it starts with that.
    docId = docId.replace('BF_', '');
    // handle case where id has _CW and ends with stuff after _CW
    docId = !docId.includes('_CW_') ? docId : docId.split('_CW_')[0].concat('_CW');
    // strip off _XY if it ends that way
    docId = docId.match(/_[A-Z][A-Z]$/) ? docId.replace(/_[A-Z][A-Z]$/, '') : docId;
    return docIdToFriendlyName[docId] || fileName;
};
//# sourceMappingURL=index.js.map